import axios, { CancelToken } from 'axios';

import { IBancoVanBancaria, IContasEPlanilhaResponse } from '../../../../pages/SaldoDeContas/interfaces';
import { IOrgao, IOrgaoManutencaoAtivacao, IOrgaoVinculado } from '../../../../pages/SaldoDeContas/Orgaos/interfaces';
import { IOrdenacaoCategoriaItem } from '../../../../pages/SaldoDeContas/SolicitacoesDeAtivacao/Gerenciar/Tabs/AlinhamentoDeContasEFontes/interfaces';
import { IAutorizacao, IConfiguracaoContabil, IContaSaldoDeContas, IFonteSaldoDeContas, IOrgaoAutorizacaoItem, IOrgaoComErro, ISistemaContabil, IUsuarioSolicitacao } from '../../../../pages/SaldoDeContas/SolicitacoesDeAtivacao/interfaces';
import { IInvestimentoAtivacao } from '../../../../pages/SaldoDeContas/InvestimentosACadastrar/interfaces';

import { GATEWAY_API_URL } from "../../../request/urls";
import { getToken } from "../../../token";

class OrgaoService {

    async getOrgaos(signal?: CancelToken): Promise<IOrgao[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            cancelToken: signal
        });

        return data.orgaos;
    }

    async getOrgaoById({
        orgaoId,
        signal
    }: {
        orgaoId: number,
        signal?: CancelToken
    }): Promise<IOrgaoManutencaoAtivacao> {
        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            cancelToken: signal
        });

        return data;
    }

    async createOrgao(values: {
        nome: string
    }): Promise<IOrgao> {

        const { data } = await axios.post(`${GATEWAY_API_URL}/saldo-empenhos/orgao`, values, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return {
            ...data,
            orgaosVinculados: data.orgaosVinculados || [],
        };
    }

    async updateOrgao({ orgaoId, ...values }: {
        orgaoId: number,
        nome: string
    }): Promise<IOrgao> {

        const { data } = await axios.put(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}`, values, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return {
            ...data,
            orgaosVinculados: data.orgaosVinculados || [],
        };
    }

    async createOrgaoVinculado({ orgaoId, ...values }: {
        orgaoId: number,
        nome: string,
        cnpj: string
    }): Promise<IOrgaoVinculado> {
        const { data } = await axios.post(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/vinculado`, values, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async updateOrgaoVinculado({ orgaoId, orgaoVinculadoId, ...values }: {
        orgaoId: number,
        orgaoVinculadoId: number,
        nome: string,
        cnpj: string
    }): Promise<IOrgaoVinculado> {
        const { data } = await axios.put(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/vinculado/${orgaoVinculadoId}`, values, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async deleteOrgaoVinculado({ orgaoId, orgaoVinculadoId }: {
        orgaoId: number,
        orgaoVinculadoId: number,
    }): Promise<IOrgaoVinculado> {
        const { data } = await axios.delete(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/vinculado/${orgaoVinculadoId}`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async getOrgaosVinculadosByOrgaoId({
        orgaoId,
        signal,
    }: {
        orgaoId: number,
        signal?: AbortSignal,
    }): Promise<IOrgaoVinculado[] | null> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/orgao-vinculado`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.orgaosVinculados || null;
    }

    async updateSistemaContabil({ orgaoId, sistemaContabilId }: {
        orgaoId: number,
        sistemaContabilId: number,
    }): Promise<ISistemaContabil> {
        const { data } = await axios.patch(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/sistema-contabil`, { sistemaContabilId }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data.solicitacaoAtivacao.sistemaContabil;
    }

    async createConfiguracaoSistemaContabil({
        orgaoId,
        configuracao,
    }: {
        orgaoId: number,
        configuracao: Object,
    }): Promise<IConfiguracaoContabil> {

        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/configuracao-sistema-contabil`,
            configuracao,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data.solicitacaoAtivacao.sistemaContabil;
    }

    async updateConfiguracaoSistemaContabil({
        orgaoId,
        configuracao,
    }: {
        orgaoId: number,
        configuracao: Object,
    }): Promise<IConfiguracaoContabil> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/configuracao-sistema-contabil`,
            configuracao,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data.solicitacaoAtivacao.configuracaoContabil;
    }

    async deleteConfiguracaoSistemaContabil(orgaoId: number): Promise<any> {

        const { data } = await axios.delete(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/configuracao-sistema-contabil`,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async updateTipoEmpenhosERestos({
        orgaoId,
        tipo,
    }: {
        orgaoId: number,
        tipo: string,
    }): Promise<string> {

        const { data } = await axios.patch(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/tipo-empenhos-e-restos`,
            { tipo },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data.solicitacaoAtivacao.tipoEmpenhosERestos;
    }

    async updateSistemaContabilIntegrado({
        orgaoId,
        integrado,
    }: {
        orgaoId: number,
        integrado: boolean,
    }): Promise<boolean> {

        const { data } = await axios.patch(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/empenhos-e-restos`,
            { integrado },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data.solicitacaoAtivacao.empenhosERestos;
    }

    async getOrdenacaoFontes({
        orgaoId
    }: {
        orgaoId: number,
    }): Promise<IOrdenacaoCategoriaItem[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/fonte-consolidada`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data.categorias;
    }

    async getFontesOrfas({
        orgaoId
    }: {
        orgaoId: number,
    }): Promise<IOrdenacaoCategoriaItem[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/fonte-orfa`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data.categorias;
    }

    async getContasEPlanilha({
        orgaoId,
        signal
    }: {
        orgaoId: number,
        signal?: AbortSignal
    }): Promise<IContasEPlanilhaResponse> {
        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/conta`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async atualizarFonteFavorita({
        orgaoId,
        numeroFonte,
        favorita,
    }: {
        orgaoId: number,
        numeroFonte: string,
        favorita: boolean,
    }): Promise<unknown> {

        const { data } = await axios.put(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/fonte-consolidada`, {
            numero: numeroFonte.split(','),
            favorita,
        }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async atualizarOrdenacaoFontes({
        orgaoId,
        numerosFontes,
        categoriaId,
    }: {
        orgaoId: number,
        numerosFontes: string[],
        categoriaId: number,
    }): Promise<IOrdenacaoCategoriaItem> {

        const { data } = await axios.post(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/fonte-consolidada/categoria/${categoriaId}`, {
            fontes: numerosFontes.map((fonte) => ({
                numero: fonte.split(','),
            })),
        }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async updateCategoriaFonte({
        orgaoId,
        numero,
        categoriaId,
    }: {
        orgaoId: number,
        numero: string,
        categoriaId: number,
    }): Promise<void> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/categoria-fonte-consolidada`,
            {
                numero: numero.split(','),
                categoriaId,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async updateDescricaoFonte({
        orgaoId,
        numero,
        descricao,
    }: {
        orgaoId: number,
        numero: string,
        descricao: string,
    }): Promise<IFonteSaldoDeContas | null> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/fonte`,
            {
                numero,
                descricao,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        const fontes = data.fontes as IFonteSaldoDeContas[];

        return fontes.find((e) => e.numero === numero) || null;
    }

    async updateDescricaoFonteConsolidada({
        orgaoId,
        numero,
        descricao,
    }: {
        orgaoId: number,
        numero: string,
        descricao: string,
    }): Promise<IFonteSaldoDeContas | null> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/nome-fonte-consolidada`,
            {
                numero: numero.split(','),
                nome: descricao,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async updateConta({
        orgaoId,
        contaId,
        orgaoVinculadoId,
        descricao,
        numero,
        agencia,
        bancoId,
        fonteIds,
    }: {
        orgaoId: number,
        contaId: number,
        orgaoVinculadoId: number,
        descricao: string,
        numero: string,
        agencia: string,
        bancoId: number,
        fonteIds: number[],
    }): Promise<IContaSaldoDeContas> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/conta/${contaId}`,
            {
                orgaoVinculadoId,
                descricao,
                numero,
                agencia,
                bancoId,
                fonteIds,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async deleteConta({
        orgaoId,
        contaId,
    }: {
        orgaoId: number,
        contaId: number,
    }): Promise<IContaSaldoDeContas> {
        const { data } = await axios.delete(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/conta/${contaId}`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async deleteFonte({
        orgaoId,
        fonteId,
    }: {
        orgaoId: number,
        fonteId: number,
    }): Promise<void> {
        await axios.delete(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/fonte/${fonteId}`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });
    }

    async updateContasFontes({
        orgaoId,
        contaIds,
        fonteIds,
    }: {
        orgaoId: number,
        contaIds: number[],
        fonteIds: number[],
    }): Promise<IContaSaldoDeContas[]> {
        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/contas/fontes`,
            {
                contaIds,
                fonteIds,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async getAutorizacoesBancoDoBrasil({
        orgaoId,
        signal
    }: {
        orgaoId: number,
        signal?: AbortSignal
    }): Promise<IOrgaoAutorizacaoItem[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-banco-do-brasil`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.orgaosVinculadosAAutorizar.map((e: any) => ({
            ...e,
            concluidaEm: e.autorizacaoBB ? e.autorizacaoBB.concluidaEm : null,
        }));
    }

    async getContasComErro({
        orgaoId,
        signal
    }: {
        orgaoId: number,
        signal?: AbortSignal
    }): Promise<IOrgaoComErro[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/contas-com-erro`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.orgaosVinculados;
    }

    async getContasInativas({
        orgaoId,
        signal
    }: {
        orgaoId: number,
        signal?: AbortSignal
    }): Promise<IOrgaoComErro[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/contas-inativas`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.orgaosVinculados;
    }

    async createIdAplicacao({
        orgaoId,
        orgaoVinculadoId,
        bbAplicacaoId,
        versaoApi,
    }: {
        orgaoId: number,
        orgaoVinculadoId: number,
        bbAplicacaoId: string,
        versaoApi: number,
    }): Promise<IAutorizacao> {
        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-banco-do-brasil`,
            { orgaoVinculadoId, bbAplicacaoId, versaoApi },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async updateIdAplicacao({
        orgaoId,
        autorizacaoBbId,
        bbAplicacaoId,
        versaoApi,
    }: {
        orgaoId: number,
        autorizacaoBbId: number,
        bbAplicacaoId: string,
        versaoApi: number,
    }): Promise<IAutorizacao> {
        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-banco-do-brasil/${autorizacaoBbId}`,
            { bbAplicacaoId, versaoApi },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async createCredenciais({
        orgaoId,
        autorizacaoBbId,
        basic,
        appKey,
        clientId,
        clientSecret,
    }: {
        orgaoId: number,
        autorizacaoBbId: number,
        basic: string,
        appKey: string,
        clientId: string,
        clientSecret: string,
    }): Promise<IAutorizacao> {
        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-banco-do-brasil/${autorizacaoBbId}/credencial`,
            { basic, appKey, clientId, clientSecret },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async updateCredenciais({
        orgaoId,
        autorizacaoBbId,
        basic,
        appKey,
        clientId,
        clientSecret,
    }: {
        orgaoId: number,
        autorizacaoBbId: number,
        basic: string,
        appKey: string,
        clientId: string,
        clientSecret: string,
    }): Promise<IAutorizacao> {
        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-banco-do-brasil/${autorizacaoBbId}/credencial`,
            { basic, appKey, clientId, clientSecret },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async deleteAutorizacao({
        orgaoId,
        autorizacaoId,
    }: {
        orgaoId: number,
        autorizacaoId: number,
    }): Promise<IAutorizacao> {
        const { data } = await axios.delete(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-banco/${autorizacaoId}`,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async getUsuarios({
        orgaoId,
        signal
    }: {
        orgaoId: number,
        signal?: AbortSignal
    }): Promise<IUsuarioSolicitacao[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/usuarios`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.usuarios;
    }

    async updateCategoriasUsuario({
        orgaoId,
        usuarioId,
        categoriaIds,
        signal
    }: {
        orgaoId: number,
        usuarioId: number,
        categoriaIds: number[],
        signal?: AbortSignal
    }): Promise<IUsuarioSolicitacao> {
        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/usuario/${usuarioId}/categorias`,
            { categoriaIds },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
                signal
            }
        );

        return {
            ...data.usuario,
            categorias: data.categorias,
        };
    }

    async getFontes({
        orgaoId,
        signal
    }: {
        orgaoId: number,
        signal?: AbortSignal
    }): Promise<IFonteSaldoDeContas[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/fonte`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.fontes;
    }

    async buscarSaldo({
        orgaoId,
    }: {
        orgaoId: number,
    }): Promise<IFonteSaldoDeContas[]> {

        const { data } = await axios.post(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/busca-saldo`, undefined, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async buscarEmpenhosERestos({
        orgaoId,
    }: {
        orgaoId: number,
    }): Promise<IFonteSaldoDeContas[]> {

        const { data } = await axios.post(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/busca-empenhos-e-restos`, undefined, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async getBancosVanBancaria({
        orgaoId,
        signal,
    }: {
        orgaoId: number,
        signal?: AbortSignal,
    }): Promise<IBancoVanBancaria[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-van`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.bancosVanAAutorizar;
    }

    async updateNumeroRequerimento({
        orgaoId,
        autorizacaoId,
        numeroRequerimento,
        signal,
    }: {
        orgaoId: number,
        autorizacaoId: number,
        numeroRequerimento: string,
        signal?: AbortSignal,
    }): Promise<IBancoVanBancaria> {

        const { data } = await axios.patch(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-van/${autorizacaoId}`, {
            numeroRequerimento
        }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async createCredenciaisVanBancaria({
        orgaoId,
        autorizacaoId,
        clientId,
        clientSecret,
        signal,
    }: {
        orgaoId: number,
        autorizacaoId: number,
        clientId: string,
        clientSecret: string,
        signal?: AbortSignal,
    }): Promise<IBancoVanBancaria> {

        const { data } = await axios.post(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-van/${autorizacaoId}/credencial`, {
            clientId,
            clientSecret,
        }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async updateCredenciaisVanBancaria({
        orgaoId,
        autorizacaoId,
        clientId,
        clientSecret,
        signal,
    }: {
        orgaoId: number,
        autorizacaoId: number,
        clientId: string,
        clientSecret: string,
        signal?: AbortSignal,
    }): Promise<IBancoVanBancaria> {

        const { data } = await axios.put(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-van/${autorizacaoId}/credencial`, {
            clientId,
            clientSecret,
        }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async getDocumentoVanBancariaSignedUrl({
        orgaoId,
        autorizacaoId,
        documentoId,
        signal,
    }: {
        orgaoId: number,
        autorizacaoId: number,
        documentoId: number,
        signal?: AbortSignal,
    }): Promise<{ url?: string; link?: string } | any> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/autorizacao-van/${autorizacaoId}/documento/${documentoId}/link`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async getInvestimentos({
        orgaoId,
        signal,
    }: {
        orgaoId: number,
        signal?: CancelToken,
    }): Promise<IInvestimentoAtivacao[]> {
        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/orgao/${orgaoId}/investimento`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            cancelToken: signal
        });

        return data.investimentos;
    }

    async encerrarConta(contaId: number): Promise<void> {
        await axios.patch(`${GATEWAY_API_URL}/saldo-empenhos/painel/conta/${contaId}/encerrar`, undefined, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });
    }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new OrgaoService();
